import {useCallback, useContext, useRef, useState} from "react";
import {ToastContext} from "Toast";
import {useMessages} from "i18n";
import {useIsLoggedIn, useUser} from "UserProvider";
import {
	type FeedbackTopic,
	maxMessageLengthForTopic,
	tagMessageWithTopic,
} from "core/support/feedbackTopic";
import {SingleFlight} from "../utils/SingleFlight";
import {SupportRequestApi} from "./SupportRequestApi";
import {supportFramingForVisitor} from "./supportEntitlement";
import {
	type SupportRequestFailure,
	showsMailtoFallback,
	supportRequestFailureOf,
	supportRequestFailureText,
	supportRequestMessageOf,
} from "./supportRequestFailure";
import {useSupportAddress} from "./useSupportAddress";
import {
	MAX_SUPPORT_MESSAGE_LENGTH,
	type SupportFieldErrors,
	NO_SUPPORT_FIELD_ERRORS,
	hasSupportFieldError,
	isSupportDraftComplete,
	validateSupportRequestDraft,
} from "./supportRequestValidation";

/**
 * The behaviour contract of the support-request dialog (epic #26): all state + the submit action, so
 * the view (`SupportRequestDialog.tsx`) is a dumb, restyleable shell. The presentation reads these
 * fields and renders the idle form, the in-flight state, the failure, or the success moment — it owns
 * no business logic.
 */
export interface SupportRequestController {
	/** The one-line subject, which becomes the MIME subject of the support mail. */
	subject: string;
	setSubject: (value: string) => void;
	/** The problem report itself. */
	message: string;
	setMessage: (value: string) => void;
	/**
	 * What the request is about, or `null` for "not stated" — the initial value, and a valid final one.
	 * The topic is never required: an unanswered question must not block a request from being sent.
	 */
	topic: FeedbackTopic | null;
	setTopic: (value: FeedbackTopic | null) => void;
	/**
	 * How many characters the user may actually type, once the chosen topic's inbox tag is deducted
	 * from the api's bound. The view drives its counter, its `maxLength` and its error text from THIS
	 * number rather than the raw constant, so all three agree with what the api will measure.
	 */
	maxMessageLength: number;
	/**
	 * The account address the answer will go to, or `""` before the account is known. There is no
	 * email INPUT on this form by design — the api reads the requester from the bearer token — so this
	 * is stated to the user rather than collected from them.
	 */
	replyToAddress: string;
	/**
	 * True when the signed-in user's plan carries the `prioritySupport` capability. Drives the priority
	 * framing and the SLA promise; the decision itself lives in `supportEntitlement.ts`.
	 */
	priority: boolean;
	/**
	 * True while a send is in flight. Drives the spinner and the Send button's `aria-disabled`; the
	 * button is never DISABLED for it, because a disabled button drops the focus it holds out of the
	 * dialog (kitecorp/transferify-ui#3076). A press meanwhile joins the send already in flight.
	 */
	submitting: boolean;
	/** True after a successful send — the view swaps to the calm "Request received" moment. */
	submitted: boolean;
	/** The classified outcome of the last failed send, or null. */
	failure: SupportRequestFailure | null;
	/**
	 * The sentence to show for that failure — the api's own explanation when it gave one, this
	 * dialog's copy otherwise — or null when the last send did not fail.
	 */
	failureText: string | null;
	/**
	 * The support inbox for the storefront the visitor is on, rendered as the `mailto:` escape hatch
	 * when {@link showMailtoFallback} is set. Derived from the host, never hardcoded: the api routes
	 * support mail per brand (api#1481), so a .cloud visitor sent to the .ro inbox would be given an
	 * address their own request was not delivered to.
	 */
	supportAddress: string;
	/** True when {@link failure} is the one a direct email can rescue (the undeliverable 502). */
	showMailtoFallback: boolean;
	/** Per-field verdict from the last submit — an error KEY per field, or null. */
	fieldErrors: SupportFieldErrors;
	/**
	 * Whether both required fields hold text, which is what arms the Send button. It stays true while
	 * the draft's own send is in flight; {@link submitting} reports that. The rule is
	 * {@link isSupportDraftComplete}.
	 */
	draftComplete: boolean;
	/** Validate + POST the request, then toast and flip to the success moment. */
	submit: () => Promise<void>;
	/** Clear the success/failure state so a re-opened dialog shows a fresh form. */
	reset: () => void;
}

/**
 * Support-request state machine — a thin React adapter over {@link validateSupportRequestDraft},
 * {@link supportFramingForUser}, {@link supportRequestFailureOf} and the single
 * {@link SupportRequestApi.send} call. It holds no rules of its own.
 *
 * Three behaviours are deliberate:
 *
 * - **Nothing the user can fix reaches the network.** Every rejection `POST /support/request` can
 *   raise on the draft is mirrored client-side and reported per FIELD, so a 400 on the subject's
 *   length or its control characters never surfaces as an opaque "try again" that could not work.
 * - **Failures are reported IN the dialog, not through the error toast.** The undeliverable case has
 *   to carry a clickable address, and a toast saying one thing while the panel says another is worse
 *   than one clear message. The view renders the failure in a `role="alert"` block, so it is still
 *   announced. The SUCCESS toast is kept, matching the feedback dialog.
 * - **The typed request survives a failure.** Every failure path leaves `subject` and `message`
 *   untouched; only a successful send clears them.
 *
 * Priority framing reads through {@link useIsLoggedIn} rather than the raw user, so the pre-render and
 * the first client render agree (SSG hydration safety) — an SLA promise must never flash in or out.
 * The rule itself is {@link supportFramingForVisitor}, shared with the Resources row that LABELS this
 * dialog (#520), so the label and the framing behind it cannot disagree.
 */
export function useSupportRequest(): SupportRequestController {
	const m = useMessages();
	const {showSuccessToast} = useContext(ToastContext);
	const [user] = useUser();
	const isLoggedIn = useIsLoggedIn();

	const [subject, setSubjectState] = useState("");
	const [message, setMessageState] = useState("");
	const [topic, setTopicState] = useState<FeedbackTopic | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [failure, setFailure] = useState<SupportRequestFailure | null>(null);
	// The api's own sentence for that failure, kept beside the kind rather than folded into it: the
	// kind still decides the mailto affordance even when the sentence on screen is the server's.
	const [failureMessage, setFailureMessage] = useState<string | null>(null);
	const [fieldErrors, setFieldErrors] = useState<SupportFieldErrors>(NO_SUPPORT_FIELD_ERRORS);

	// Synchronous admission gate: the active send is published before `submit` returns, so a hammered
	// button joins the ONE request in flight rather than waiting for `submitting` to reach the DOM.
	const submitFlight = useRef<SingleFlight | null>(null);
	submitFlight.current ??= new SingleFlight();

	const priority = supportFramingForVisitor(isLoggedIn, user) === "priority";
	const supportAddress = useSupportAddress();

	// Editing a field retracts its complaint: an error the user is actively fixing is noise, and a
	// stale one next to corrected input reads as "still wrong".
	const setSubject = useCallback((value: string) => {
		setSubjectState(value);
		setFieldErrors(current => (current.subject === null ? current : {...current, subject: null}));
	}, []);

	const setMessage = useCallback((value: string) => {
		setMessageState(value);
		setFieldErrors(current => (current.message === null ? current : {...current, message: null}));
	}, []);

	// Retracts the MESSAGE's complaint, not one of its own — the topic is the only control that can
	// change the message's verdict without the message being touched, because it moves the budget the
	// length was judged against. Leaving the old verdict up would state a limit the field no longer has.
	const setTopic = useCallback((value: FeedbackTopic | null) => {
		setTopicState(value);
		setFieldErrors(current => (current.message === null ? current : {...current, message: null}));
	}, []);

	const draftComplete = isSupportDraftComplete({subject, message});
	const maxMessageLength = maxMessageLengthForTopic(topic, MAX_SUPPORT_MESSAGE_LENGTH);

	const submit = useCallback((): Promise<void> => submitFlight.current!.run(async () => {
		// The TYPED message against the budget the topic leaves — equivalent to measuring the tagged
		// body against the api's bound, but it reports the length the user can see and act on.
		const verdict = validateSupportRequestDraft({subject, message}, maxMessageLength);
		setFieldErrors(verdict);
		if (hasSupportFieldError(verdict)) {
			return;
		}
		setSubmitting(true);
		setFailure(null);
		setFailureMessage(null);
		try {
			// The topic rides as a tagged first line in the MESSAGE, deliberately not in the request's
			// `category`: that field is a closed api enum (`GENERAL` | `EU_WITHDRAWAL`) whose withdrawal
			// value triggers a statutory acknowledgement, so product topics cannot be mapped onto it
			// without either losing the topic or firing that acknowledgement. Omitting `category`
			// therefore leaves the api's own default (`GENERAL`) exactly as it was.
			await SupportRequestApi.send({subject, message: tagMessageWithTopic(topic, message)});
			setSubjectState("");
			setMessageState("");
			// Cleared with the request it described: a second request inheriting the first one's topic
			// would mislabel it, and the user has no reason to expect the control to still be set. The
			// RAW setter, because this is the form emptying itself rather than the user editing a field.
			setTopicState(null);
			setSubmitted(true);
			showSuccessToast(m.support.toasts.sent);
		} catch (error) {
			setFailure(supportRequestFailureOf(error));
			setFailureMessage(supportRequestMessageOf(error));
		} finally {
			setSubmitting(false);
		}
	}), [subject, message, topic, maxMessageLength, m, showSuccessToast]);

	const reset = useCallback(() => {
		setSubmitted(false);
		setFailure(null);
		setFailureMessage(null);
		setFieldErrors(NO_SUPPORT_FIELD_ERRORS);
	}, []);

	return {
		subject, setSubject, message, setMessage, topic, setTopic, maxMessageLength,
		// Read through `useIsLoggedIn` for the same SSG-hydration reason as the priority framing above:
		// a logged-in visitor is logged-OUT during the pre-render and the first client render, so
		// reading the raw user here would make an address appear on the second render.
		replyToAddress: isLoggedIn && user.email ? user.email : "",
		priority, submitting, submitted, failure,
		failureText: failure === null
			? null
			: supportRequestFailureText(failure, failureMessage, m.support.failures),
		supportAddress,
		showMailtoFallback: failure !== null && showsMailtoFallback(failure),
		fieldErrors, draftComplete, submit, reset,
	};
}
